"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var jsx_runtime_1 = require("@emotion/react/jsx-runtime");
/** @jsxImportSource @emotion/react */
var Modal_1 = __importDefault(require("@mui/material/Modal"));
var Box_1 = __importDefault(require("@mui/material/Box"));
var MenuItem_1 = __importDefault(require("@mui/material/MenuItem"));
var InputBase_1 = __importDefault(require("@mui/material/InputBase"));
var IconButton_1 = __importDefault(require("@mui/material/IconButton"));
var Close_1 = __importDefault(require("@mui/icons-material/Close"));
var react_1 = require("react");
var react_2 = require("@emotion/react");
var countryData_1 = require("../../utils/countryData");
var FlagIcon_1 = __importDefault(require("./FlagIcon"));
var CountryModal = function (_a) {
    var onCountryChange = _a.onCountryChange, _b = _a.enableCountrySearch, enableCountrySearch = _b === void 0 ? false : _b, open = _a.open, onClose = _a.onClose, _c = _a.searchPlaceholder, searchPlaceholder = _c === void 0 ? 'Search countries...' : _c, _d = _a.modalHeading, modalHeading = _d === void 0 ? 'Select Country' : _d, providedCountries = _a.countries;
    var _e = (0, react_1.useState)(''), searchTerm = _e[0], setSearchTerm = _e[1];
    var _f = (0, react_1.useState)(-1), selectedIndex = _f[0], setSelectedIndex = _f[1];
    var _g = (0, react_1.useState)(false), isClosing = _g[0], setIsClosing = _g[1];
    var searchInputRef = (0, react_1.useRef)(null);
    var theme = (0, react_2.useTheme)();
    var countries = (0, react_1.useMemo)(function () { return providedCountries || (0, countryData_1.getCountryData)(); }, [providedCountries]);
    var filteredCountries = (0, react_1.useMemo)(function () {
        if (!searchTerm.trim())
            return countries;
        var term = searchTerm.toLowerCase().trim();
        return countries.filter(function (country) {
            return country.name.toLowerCase().includes(term) ||
                country.dialCode.toLowerCase().includes(term) ||
                country.code.toLowerCase().includes(term);
        });
    }, [countries, searchTerm]);
    // Reset search state when modal opens/closes
    (0, react_1.useEffect)(function () {
        if (open) {
            setSearchTerm('');
            setSelectedIndex(-1);
            setIsClosing(false);
        }
    }, [open]);
    // Auto-focus search input when modal opens and blur when closing
    (0, react_1.useEffect)(function () {
        if (open && enableCountrySearch) {
            var timer_1 = setTimeout(function () {
                var _a;
                (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 150); // Slightly longer delay to ensure modal is fully rendered
            return function () { return clearTimeout(timer_1); };
        }
        else if (!open) {
            // When closing, ensure any focused element inside the modal is blurred
            // to prevent aria-hidden violations
            var activeElement = document.activeElement;
            if (activeElement && activeElement.closest('[role="dialog"]')) {
                activeElement.blur();
            }
        }
        return undefined;
    }, [open, enableCountrySearch]);
    var handleCountrySelect = (0, react_1.useCallback)(function (country) {
        // Blur any focused element to prevent aria-hidden violations
        var activeElement = document.activeElement;
        if (activeElement && activeElement.closest('[role="dialog"]')) {
            activeElement.blur();
        }
        onCountryChange(country);
        setIsClosing(true);
        setTimeout(function () {
            onClose();
            setIsClosing(false);
        }, 195);
    }, [onCountryChange, onClose]);
    var handleModalClose = (0, react_1.useCallback)(function () {
        // Blur any focused element to prevent aria-hidden violations
        var activeElement = document.activeElement;
        if (activeElement && activeElement.closest('[role="dialog"]')) {
            activeElement.blur();
        }
        setIsClosing(true);
        setTimeout(function () {
            onClose();
            setIsClosing(false);
        }, 195);
    }, [onClose]);
    var handleSearchChange = (0, react_1.useCallback)(function (event) {
        var value = event.target.value;
        setSearchTerm(value);
        setSelectedIndex(-1);
    }, []);
    var handleKeyDown = (0, react_1.useCallback)(function (event) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setSelectedIndex(function (prev) {
                    return prev < filteredCountries.length - 1 ? prev + 1 : prev;
                });
                break;
            case 'ArrowUp':
                event.preventDefault();
                setSelectedIndex(function (prev) { return (prev > 0 ? prev - 1 : -1); });
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredCountries.length) {
                    var country = filteredCountries[selectedIndex];
                    handleCountrySelect(country);
                }
                break;
            case 'Escape':
                event.preventDefault();
                handleModalClose();
                break;
        }
    }, [
        filteredCountries.length,
        selectedIndex,
        handleCountrySelect,
        handleModalClose,
    ]);
    var modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        height: '90%',
        bgcolor: theme.mvf.palette.background.answerBackgroundColor,
        boxShadow: theme.mvf.boxShadow.fierce,
        borderRadius: 1,
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
    };
    return ((0, jsx_runtime_1.jsx)(Modal_1.default, { open: open, onClose: handleModalClose, "aria-labelledby": "country-selection-modal", "aria-describedby": "country-selection-modal-description", closeAfterTransition: true, disableRestoreFocus: true, disableEnforceFocus: true, hideBackdrop: true, children: (0, jsx_runtime_1.jsxs)(Box_1.default, { role: "dialog", sx: __assign(__assign({}, modalStyle), { animation: isClosing
                    ? 'modalExit 195ms cubic-bezier(0.4, 0, 1, 1)'
                    : 'modalEnter 225ms cubic-bezier(0.4, 0, 0.2, 1)', '@keyframes modalEnter': {
                    '0%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.8)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    },
                }, '@keyframes modalExit': {
                    '0%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    },
                    '100%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.8)',
                    },
                } }), children: [(0, jsx_runtime_1.jsxs)("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        backgroundColor: '#ffffff',
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px',
                    }, children: [(0, jsx_runtime_1.jsx)("h2", { style: {
                                fontSize: '18px',
                                fontWeight: 500,
                                color: 'rgba(0, 0, 0, 0.87)',
                                margin: 0,
                            }, children: modalHeading }), (0, jsx_runtime_1.jsx)(IconButton_1.default, { onClick: handleModalClose, size: "small", sx: {
                                color: 'rgba(0, 0, 0, 0.6)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }, "aria-label": "Close", children: (0, jsx_runtime_1.jsx)(Close_1.default, {}) })] }), enableCountrySearch && ((0, jsx_runtime_1.jsx)("div", { style: {
                        padding: '16px 20px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        backgroundColor: '#ffffff',
                    }, children: (0, jsx_runtime_1.jsx)(InputBase_1.default, { ref: searchInputRef, placeholder: searchPlaceholder, value: searchTerm, onChange: handleSearchChange, onKeyDown: handleKeyDown, fullWidth: true, sx: {
                            fontSize: '16px',
                            padding: '8px 12px',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            '&:focus-within': {
                                borderColor: '#1976d2',
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
                            },
                        }, inputProps: {
                            'aria-label': 'Search countries',
                            autoComplete: 'off',
                            spellCheck: false,
                        } }) })), (0, jsx_runtime_1.jsx)("div", { style: {
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                    }, role: "listbox", "aria-label": "Country list", children: filteredCountries.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { style: {
                            padding: '20px',
                            textAlign: 'center',
                            color: 'rgba(0, 0, 0, 0.6)',
                        }, children: "No countries found" })) : (filteredCountries.map(function (country, index) { return ((0, jsx_runtime_1.jsx)(MenuItem_1.default, { selected: index === selectedIndex, onClick: function () { return handleCountrySelect(country); }, onMouseEnter: function () { return setSelectedIndex(index); }, role: "option", "aria-selected": index === selectedIndex, sx: {
                            padding: '12px 20px',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }, children: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(FlagIcon_1.default, { countryCode: country.code, style: { marginRight: '12px' } }), (0, jsx_runtime_1.jsx)("span", { style: { flex: 1, fontSize: '16px' }, children: country.name }), (0, jsx_runtime_1.jsx)("span", { style: { opacity: 0.7, fontSize: '14px' }, children: country.dialCode })] }) }, country.code)); })) })] }) }));
};
exports.default = CountryModal;
//# sourceMappingURL=CountryModal.js.map